/**
 * Query Keys Factory
 * Centralized management of all query keys for type safety and consistency
 */

export const queryKeys = {
  // Auth related queries
  auth: {
    all: ["auth"] as const,
    currentUser: () => [...queryKeys.auth.all, "current-user"] as const,
    userProfile: () => [...queryKeys.auth.all, "user-profile"] as const,
    session: () => [...queryKeys.auth.all, "session"] as const,
  },

  // Validator related queries
  validators: {
    all: ["validators"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.validators.all, "list", filters] as const,
    detail: (id: string) =>
      [...queryKeys.validators.all, "detail", id] as const,
    applications: {
      all: ["validators", "applications"] as const,
      list: () => ["validators", "applications", "list"] as const,
      statistics: (walletAddress: string) =>
        [
          ...queryKeys.validators.applications.all,
          "statistics",
          walletAddress,
        ] as const,
      listByWallet: (wallet: string) =>
        [...queryKeys.validators.applications.all, "list", wallet] as const,
      detail: (id: string) =>
        ["validators", "applications", "detail", id] as const,
      applicationDataAfterStake: (applicationId: string, wallet: string) =>
        [
          "validators",
          "applications",
          "application-data-after-stake",
          applicationId,
          wallet,
        ] as const,
      managedFinancialCards: (applicationId: string, wallet: string) =>
        [
          "validators",
          "applications",
          "managed-financial-cards",
          applicationId,
          wallet,
        ] as const,
      managedUnstakeTx: (applicationId: string, wallet: string) =>
        [
          "validators",
          "applications",
          "managed-unstake-tx",
          applicationId,
          wallet,
        ] as const,
      managedClaimTx: (applicationId: string, wallet: string) =>
        [
          "validators",
          "applications",
          "managed-claim-tx",
          applicationId,
          wallet,
        ] as const,
      unstakeInfo: (id: string) =>
        ["validators", "applications", "unstake", "info", id] as const,
    },
  },

  // Pool related queries
  pools: {
    all: ["pools"] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.pools.all, "list", filters] as const,
    createdPools: () => [...queryKeys.pools.all, "created-pools"] as const,
    manager: (wallet: string) =>
      [...queryKeys.pools.all, "manager", wallet] as const,
    detail: (id: string) => [...queryKeys.pools.all, "detail", id] as const,
  },

  // Admin related queries
  admin: {
    all: ["admin"] as const,
    applications: {
      all: ["admin", "applications"] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.applications.all, "list", filters] as const,
      detail: (id: string) =>
        [...queryKeys.admin.applications.all, "detail", id] as const,
    },
    management: {
      all: ["admin", "management"] as const,
      stats: () => [...queryKeys.admin.management.all, "stats"] as const,
      users: {
        all: ["admin", "management", "users"] as const,
        list: (filters?: Record<string, unknown>) =>
          [...queryKeys.admin.management.users.all, "list", filters] as const,
        detail: (id: string) =>
          [...queryKeys.admin.management.users.all, "detail", id] as const,
      },
    },
    contact: {
      all: ["admin", "contact"] as const,
      list: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.contact.all, "list", filters] as const,
      detail: (id: string) =>
        [...queryKeys.admin.contact.all, "detail", id] as const,
    },
    sharedStake: {
      all: ["admin", "sharedStake"] as const,
      staked: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.sharedStake.all, "staked", filters] as const,
      unstaked: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.sharedStake.all, "unstaked", filters] as const,
      nodes: (filters?: Record<string, unknown>) =>
        [...queryKeys.admin.sharedStake.all, "nodes", filters] as const,
      stats: () => [...queryKeys.admin.sharedStake.all, "stats"] as const,
      info: () => [...queryKeys.admin.sharedStake.all, "info"] as const,
    },
    dashboard: {
      all: ["admin", "dashboard"] as const,
      statistics: () =>
        [...queryKeys.admin.dashboard.all, "statistics"] as const,
      stakeSummary: () =>
        [...queryKeys.admin.dashboard.all, "stakeSummary"] as const,
      financialSummary: () =>
        [...queryKeys.admin.dashboard.all, "financialSummary"] as const,
      recentValidator: () =>
        [...queryKeys.admin.dashboard.all, "recentValidator"] as const,
      unbondingRequest: () =>
        [...queryKeys.admin.dashboard.all, "unbondingRequest"] as const,
      overview: () => [...queryKeys.admin.dashboard.all, "overview"] as const,
    },
  },

  // User shared staking queries
  sharedStaking: {
    all: ["sharedStaking"] as const,
    stakeHistory: (wallet: string) =>
      [...queryKeys.sharedStaking.all, "stakeHistory", wallet] as const,
    unstakeHistory: (wallet: string) =>
      [...queryKeys.sharedStaking.all, "unstakeHistory", wallet] as const,
    unstakeDetails: (unstakeId: string) =>
      [...queryKeys.sharedStaking.all, "unstakeDetails", unstakeId] as const,
    pendingUnstakeInfo: (wallet: string) =>
      [...queryKeys.sharedStaking.all, "pendingUnstakeInfo", wallet] as const,
    stakeDetails: (stakeId: string) =>
      [...queryKeys.sharedStaking.all, "stakeDetails", stakeId] as const,
    summary: (wallet: string) =>
      [...queryKeys.sharedStaking.all, "summary", wallet] as const,
    stats: (wallet: string) =>
      [...queryKeys.sharedStaking.all, "stats", wallet] as const,
    claimRewardTx: () =>
      [...queryKeys.sharedStaking.all, "claimRewardTx"] as const,
    unstakeTx: (unstakeId: string) =>
      [...queryKeys.sharedStaking.all, "unstakeTx", unstakeId] as const,
    cancelUnstakeTx: (unstakeId: string, wallet: string) =>
      [
        ...queryKeys.sharedStaking.all,
        "cancelUnstakeTx",
        unstakeId,
        wallet,
      ] as const,
  },
  notifications: {
    all: ["notifications"] as const,
    list: (wallet: string, page = 1) =>
      [...queryKeys.notifications.all, "user", wallet, page] as const,
    admin: (page = 1) =>
      [...queryKeys.notifications.all, "admin", page] as const,
  },

  userDashboard: {
    all: ["userDashboard"] as const,
    activeValidators: (wallet: string) =>
      [...queryKeys.userDashboard.all, "activeValidators", wallet] as const,
    performanceSummary: (wallet: string) =>
      [...queryKeys.userDashboard.all, "performanceSummary", wallet] as const,
    statistics: (wallet: string) =>
      [...queryKeys.userDashboard.all, "statistics", wallet] as const,
  },
} as const;
